/** Short fades kill HTMLAudioElement start/stop/switch/seek clicks. */

export const AUDIO_FADE_MS = 70;
/** Gain ramps for volume slider / mute / seek restore. */
export const AUDIO_GAIN_FADE_MS = 50;

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/** Linear volume ramp via rAF. Aborts early when `isActive` becomes false. */
export async function fadeAudioVolume(
  audio: Pick<HTMLAudioElement, 'volume'>,
  to: number,
  durationMs: number,
  isActive: () => boolean = () => true,
): Promise<void> {
  const target = clamp01(to);
  const from = clamp01(audio.volume);
  if (!isActive()) return;
  if (durationMs <= 0 || from === target) {
    audio.volume = target;
    return;
  }

  const started = performance.now();
  await new Promise<void>((resolve) => {
    const tick = (now: number) => {
      if (!isActive()) {
        resolve();
        return;
      }
      const t = Math.min(1, (now - started) / durationMs);
      // Smoothstep eases edge discontinuities a bit vs linear.
      const eased = t * t * (3 - 2 * t);
      audio.volume = from + (target - from) * eased;
      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        audio.volume = target;
        resolve();
      }
    };
    requestAnimationFrame(tick);
  });
}
